import Image from "next/image"
import { creditFromPhotoRecord, imageCreditTitle } from "@/lib/image-credits"

/**
 * One frame in a reference-photo gallery, normalised across the data shapes
 * that feed it (WeedPagePhoto in data/weeds, ReferenceImage in
 * data/grass-reference-photos).
 */
export interface PhotoFigureFrame {
  src: string
  alt: string
  /** Short label naming the ID feature this frame shows. */
  caption?: string
  /** Photographer. null/undefined for attribution-free sources (CC0). */
  credit?: string | null
  license?: string
  sourceUrl?: string
  /** Renders an amber "Lookalike" tag ahead of the caption. */
  lookalike?: boolean
}

/**
 * Captioned reference-photo gallery, two up on desktop. Shared by the weed
 * identification galleries and the grass guide species galleries so the two
 * cannot drift apart; it keeps the figure shape the guides have always used
 * (rounded bordered frame, 3/2 crop, small caption).
 *
 * Credits never render under the frames. Each credited frame carries its credit
 * as a title tooltip; the page lists the credits once in ImageCreditsFooter.
 *
 * `unoptimized` is load-bearing: Next's optimizer would rewrite src to
 * `/_next/image`, which public/robots.txt disallows, while sitemap-image.xml
 * and ImageObject.contentUrl cite the bare /examples path. See
 * isIndexableLocalAsset in lib/image-utils.ts. The files are pre-cropped to
 * their display size, so there is nothing for the optimizer to do.
 *
 * Aspect ratio is an inline style, not an arbitrary Tailwind class, to dodge
 * the precompiled-CSS gap; see [[project_tailwind_v4_new_arbitrary_jit_gap]].
 */
export function PhotoFigureGallery({
  frames,
  heading,
}: {
  frames: PhotoFigureFrame[]
  heading?: string
}) {
  if (frames.length === 0) return null

  return (
    <div className="not-prose mt-6">
      {heading && (
        <h3 className="mb-3 mt-0 text-sm font-bold uppercase tracking-wide text-gray-500">
          {heading}
        </h3>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {frames.map((frame) => {
          const credit = creditFromPhotoRecord(frame)
          return (
            <figure key={frame.src} className="m-0">
              <div
                className="relative overflow-hidden rounded-xl border border-gray-200 bg-linear-to-br from-gray-100 to-gray-200 shadow-sm"
                style={{ aspectRatio: "3 / 2" }}
              >
                <Image
                  src={frame.src}
                  alt={frame.alt}
                  title={credit ? imageCreditTitle(credit) : undefined}
                  fill
                  loading="lazy"
                  unoptimized
                  sizes="(max-width: 768px) 100vw, 28rem"
                  className="object-cover"
                />
              </div>
              <figcaption className="mt-1.5 text-[11px] font-medium text-gray-500">
                {frame.lookalike && (
                  <span className="mr-1 font-bold uppercase tracking-wide text-amber-700">
                    Lookalike
                  </span>
                )}
                {frame.caption}
              </figcaption>
            </figure>
          )
        })}
      </div>
    </div>
  )
}
